import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GlobalStyle, StyledSearchResults } from './search-results.styles'
import { FormState } from '../../forms/hocs/form.state'
import { SearchListings } from '../../patterns/search-listings/search-listings'
import { Form } from '../../forms/hocs/form.context'
import { SavedAdvertsQueryContext } from '../../contexts/saved-adverts-context'
import { FilterErrorType, isFilterError, isPostcodeFilterError } from '../../utils/query-utilities'
import { UserLocationContext } from '../../contexts/user-location-context'
import { Search } from 'history'
import { useSearchViewState } from '../../contexts/search-view-state-context'
import { usePagesLoadedContext } from '../../contexts/pages-loaded-context'

import { SkeletonSearchListings } from '../../patterns/search-listings/search-listings.skeleton'
import { useSearchResultsQuery } from '../../hooks/search-query/use-search-results-query'
import { getPageNumbersFromListings } from '../../utils/search/get-page-number-from-listings'
import { useAppContext } from '@at/sauron-platform-core'
import { initGpt } from '../../components/gpt-insearch/gpt-insearch'
import {
    SearchResultsListingsGridQuery_searchResults_SearchResults,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { PostcodeError } from '../../components/postcode-error/postcode-error'
import { ZeroResults } from '../../components/zero-results/zero-results.hoc'
import { BackToDealer } from '../../patterns/search-results/back-to-dealer'
import { SearchFacets } from '../../patterns/search-facets/search-facets'
import { SessionKeys } from '../../utils/session-keys'
import { uuidProvider } from './uuid-provider'

const defaultPreModalResultsState = { listings: [] }

const dealerLinkFromListings = (
    listings: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings> = []
): string | undefined => {
    if (listings.length > 0) {
        return (
            (listings[0] as SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing)
                .dealerLink || undefined
        )
    }
}

const SearchQueryPage: FC = () => {
    const { data, loading, networkStatus, error, totalResults, previousData, fetchMore } = useSearchResultsQuery()
    const { search } = useLocation()
    const { savedAdvertsList } = useContext(SavedAdvertsQueryContext)
    const { error: hasUserLocationError } = useContext(UserLocationContext)
    const { setLoadedPages } = usePagesLoadedContext()
    const { showSearchFilter } = useSearchViewState()
    const hasPostcodeFilterError = isPostcodeFilterError(error)
    const [searchId, setSearchId] = useState('')

    const [preModalResults, setPreModalResults] =
        useState<SearchResultsListingsGridQuery_searchResults_SearchResults>(defaultPreModalResultsState)

    const numberOfPages = data?.searchResults?.page?.count || previousData?.searchResults?.page?.count || 0
    const queryPage = data?.searchResults?.page?.number || previousData?.searchResults?.page?.number || 1
    const intercept = data?.searchResults?.intercept
    const { abTestGroups, ssrMode } = useAppContext()

    useEffect(() => {
        setSearchId(sessionStorage.getItem(SessionKeys.SEARCH_ID) ?? uuidProvider.getUUID())
        if (!ssrMode) {
            initGpt(abTestGroups, searchId)
        }
    }, [searchId, abTestGroups, ssrMode])

    const showZeroResultsMessage =
        !loading &&
        !isFilterError(error, FilterErrorType.PAGE) &&
        !isFilterError(error, FilterErrorType.LOCATION) &&
        totalResults === 0 &&
        !showSearchFilter &&
        !hasUserLocationError

    const listingsProps = {
        listings: preModalResults.listings,
        error,
        pageCount: numberOfPages,
        queryPage,
        savedAdverts: savedAdvertsList,
        totalResults,
        fetchMore,
        searchId,
        loading,
        networkStatus,
        showZeroResultsMessage,
        intercept,
        leasingSummary: preModalResults?.searchInfo?.leasingSummary,
    }

    const ListingSkeletons = !preModalResults.listings.length ? SkeletonSearchListings : SearchListings
    const Listings = loading ? ListingSkeletons : SearchListings

    const previousSearch = useRef<Search>()

    useEffect(() => {
        if (!loading && previousSearch.current !== search && !showSearchFilter) {
            previousSearch.current = search
        }

        setPreModalResults((prevState) =>
            showSearchFilter ? prevState : data?.searchResults || defaultPreModalResultsState
        )
    }, [loading, showSearchFilter, search, previousSearch, data?.searchResults, totalResults])

    useEffect(() => {
        if (!loading) {
            setLoadedPages(getPageNumbersFromListings(preModalResults.listings))
        }
    }, [preModalResults, loading, setLoadedPages])

    return (
        <Form>
            <FormState>
                <GlobalStyle scrollBehaviour={true} />
                <StyledSearchResults>
                    {(hasUserLocationError || hasPostcodeFilterError) && <PostcodeError />}
                    <SearchFacets />
                    {showZeroResultsMessage ? <ZeroResults /> : null}
                    <BackToDealer dealerLink={dealerLinkFromListings(listingsProps.listings)} />
                    <Listings {...listingsProps} />
                </StyledSearchResults>
            </FormState>
        </Form>
    )
}

export default SearchQueryPage
